import { Fragment } from "react";
import {
  Row,
  Col,
  Button,
  Tag,
  Tabs,
  Badge,
  Empty,
  Spin,
  Radio,
  Popover,
  Tooltip,
} from "antd";
import { Link, useLocation } from "react-router-dom";
import moment from "moment-timezone";
import uniqBy from "lodash/unionBy";
import { useTranslation } from "react-i18next";
import { getCookie } from './../../../services/cookies';
import countryRestrictions from '../../../utils/countryRestrictions.json'
import groupedExams from '../../../utils/groupedExams.json';
import { timeDiff } from '../../../services/helper';
import { HasAccess } from "@permify/react-role";
import { ExclamationCircleFilled, SyncOutlined } from '@ant-design/icons';
import settings from '../countrySettings.json';
import config from "../../../config";
import { callPatient } from '../../../services/general';
import { useState } from "react";
import { useBoxContext } from '../../../context/BoxContext';
import { message } from "antd";
import showBlockedCallModal from '../../ExamPage/components/BlockedCallModal';
import showOccupiedPatientModal from '../../ExamPage/components/OccupiedPatientModal';

const { TabPane } = Tabs;
const { format } = require("rut.js");


const ConsultationTabs = ({
  pendingConsultations,
  reevaluationConsultations,
  treatedConsultations,
  inProgressConsultations,
  loading,
  currentPage,
  setCurrentPage,
  form,
}) => {
  const location = useLocation();
  const countryCode = getCookie("country_code");
  const states = ['uncreated', 'occupied', 'incompleted', 'reevaluate', 'completed', 'done'];
  const roles = getCookie('roles');
  const isPermittedAuditorRole = roles.includes('doctor') && roles.includes('medical_auditor');
  const { t } = useTranslation();

  const { hasBoxes } = useBoxContext();

  const [loadingPerRow, setLoadingPerRow] = useState({});

  const loadMore = (event, type) => {
    const direction = event.target.value;
    const _currentPage =
      direction === "next" ? currentPage[type] + 1 : currentPage[type] - 1;
    setCurrentPage({ ...currentPage, [type]: _currentPage });
  };

  const handleCallPatient = async (admissionPatientId, forceOccupiedCall = false, forceRecentCall = false) => {
    const box = getCookie('box_id');
    if (!box || typeof box !== 'string' || box.trim() === '') {
      message.warning(t('examPage.examTabs.callPatientBoxWarning'));
      return;
    }

    setLoadingPerRow(prev => ({ ...prev, [admissionPatientId]: true }));

    try {
      await callPatient({ admissionPatientId, box, forceOccupiedCall, forceRecentCall });
      message.success(t('examPage.examTabs.callPatientSuccess'));
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : error?.message || error?.toString?.() || 'Error';

      if (errorMessage?.includes('PATIENT_OCCUPIED_IN_EXAM') && !forceOccupiedCall) {
        setLoadingPerRow((prev) => ({ ...prev, [admissionPatientId]: false }));
        showOccupiedPatientModal(errorMessage, () => handleCallPatient(admissionPatientId, true, false), t);
        return;
      }

      if (errorMessage?.includes('PATIENT_RECENTLY_CALLED') && !forceRecentCall) {
        setLoadingPerRow((prev) => ({ ...prev, [admissionPatientId]: false }));
        showBlockedCallModal(errorMessage, () => handleCallPatient(admissionPatientId, forceOccupiedCall, true), t);
        return;
      }

      message.error(errorMessage || t('examPage.examTabs.callPatientError'));
      console.error('Error callPatient', error);
    } finally {
      setLoadingPerRow(prev => ({ ...prev, [admissionPatientId]: false }));
    }
  };

  const consultationSymbols = (type) => {
    const isAuditSignActive = settings.medicalConsultation.auditorSignature[countryCode] || isPermittedAuditorRole;
    const alignment = {
      display: "flex",
      alignItems: "center",
    };
    return (
      <Row gutter={20} className="symbols-wrapper mb-20">
        <Col>
          <Row gutter={7}>
            <Col style={alignment}>
              <div
                className={`${type === "state"
                  ? "yellow-state"
                  : type !== "treated" && "orange-state"
                  }`}
              />
            </Col>
            <Col>
              <p className="mb-0">
                {
                  type === "state" ?
                    t('medicalConsultations.consultationTabs.occupiedPatient')
                    :
                    type !== "treated" &&
                    (
                      isAuditSignActive ?
                        `${t('medicalConsultations.consultationTabs.medicalConsultation')} no evaluada`
                        :
                        `${t('medicalConsultations.consultationTabs.medicalConsultation')} no firmada`
                    )
                }
              </p>
            </Col>
          </Row>
        </Col>
        <Col>
          <Row gutter={7}>
            <Col style={alignment}>
              <div
                className={`${type === "state" ? "red-state" : "green-state"}`}
              />
            </Col>
            <Col>
              <p className="mb-0">
                {type === "state"
                  ? "Exámenes no completados"
                  : `${t('medicalConsultations.consultationTabs.medicalConsultation')} completada`}
              </p>
            </Col>
          </Row>
        </Col>
        <Col>
          <Row gutter={7}>
            <Col style={alignment}>
              <div
                className={`${type === "state" && "green-state"} ${["noSignature", "inProgress"].includes(type) && "gray-state"
                  }`}
              />
            </Col>
            <Col>
              <p className="mb-0">
                {type === "state" && "Exámenes completados"}
                {
                  ["noSignature", "inProgress"].includes(type) &&
                  `Sin ${t('medicalConsultations.consultationTabs.medicalConsultation').toLocaleLowerCase()}`
                }
              </p>
            </Col>
          </Row>
        </Col>
        {type === "state" &&
          <Col>
            <Row gutter={7}>
              <Col style={alignment}>
                <div className={`${type === "state" && "gray-state"}`} />
              </Col>
              <Col>
                <p className="mb-0">
                  {type === "state" && "Exámenes a reevaluar"}
                </p>
              </Col>
            </Row>
          </Col>
        }
        {isAuditSignActive && type === 'inProgress' && (
          <Col>
            <Row gutter={7}>
              <Col style={alignment}>
                <div className={`yellow-state`} />
              </Col>
              <Col>
                <p className="mb-0">
                  {t('medicalConsultations.consultationTabs.signedByEvaluatorState')}
                </p>
              </Col>
            </Row>
          </Col>
        )}
        {isAuditSignActive && (type === "inProgress" || type === "treated") && (
          <Col>
            <Row gutter={7}>
              <Col style={alignment}>
                <div className={"auditor-sign-completed-state"} >
                  <span className="auditor-sign-letter">FA</span>
                </div>
              </Col>
              <Col>
                <p className="mb-0">
                  {t('medicalConsultations.consultationTabs.signedConsultationState')}
                </p>
              </Col>
            </Row>
          </Col>
        )}
        {isAuditSignActive && type === "inProgress" && (
          <Col>
            <Row gutter={7}>
              <Col style={alignment}>
                <div className={`auditor-sign-pending-state`} >
                  <span className="auditor-sign-letter">FA</span>
                </div>
              </Col>
              <Col>
                <p className="mb-0">
                  {t('medicalConsultations.consultationTabs.auditAvailable')}
                </p>
              </Col>
            </Row>
          </Col>
        )}
        {settings.medicalConsultation.reevaluationTags[countryCode] && (
          <Col>
            <Row gutter={7}>
              <Col style={alignment}>
                <SyncOutlined className="reevaluation-cycle-icon" />
              </Col>
              <Col>
                <p className="mb-0">
                  {t('medicalConsultations.consultationTabs.reevaluationLegend')}
                </p>
              </Col>
            </Row>
          </Col>
        )}
        {settings.medicalConsultation.autoGeneratedConsultation[countryCode] && (
          <Col>
            <Row gutter={7}>
              <Col style={alignment}>
                <div className="auto-generated-consultation-state">
                  <span className="auto-generated-badge-letter">A</span>
                </div>
              </Col>
              <Col>
                <p className="mb-0">
                  {t('medicalConsultations.consultationTabs.autoGeneratedConsultationState')}
                </p>
              </Col>
            </Row>
          </Col>
        )}

      </Row>
    );
  };

  const formatTime = (value, type, timezone) => {
    if (type === "diference") {
      const startTime = moment.tz(value, 'UTC');
      const currentTime = moment.tz(timezone);
      const newStartTime = moment(startTime.format('YYYY-MM-DD HH:mm:ss'));
      const duration = moment.duration(currentTime.diff(newStartTime));
      return `${duration.hours()}:${duration.minutes()}:${duration.seconds()}`;
    } else {
      return moment(value).format("HH:mm");
    }
  };

  const formatDate = (value) => {
    return moment(value, "YYYY-MM-DD").format("DD-MM-YYYY");
  };

  const reevaluationTypeLabel = (service) => [
    service.is_reevaluation && t('medicalConsultations.consultationTabs.reevaluationTag'),
    service.is_observation_lift && t('medicalConsultations.consultationTabs.observationLiftTag'),
  ].filter(Boolean).join(' / ');

  const labTag = (exam, service) => {
    const isAuditSignExamSymbolActive = settings.medicalConsultation.auditorSignature[countryCode]
      || (isPermittedAuditorRole && (config?.restrictedCompaniesSignIds?.split(',') || [])?.includes(exam?.company_id?.toString()))
    const showReevaluationTags = settings.medicalConsultation.reevaluationTags[countryCode];
    const isReevaluationOrLift = showReevaluationTags && (service.is_reevaluation || service.is_observation_lift);
    const isReevaluationPill = exam.type === 'reevaluation' && isReevaluationOrLift;
    const isOriginConsultation = showReevaluationTags
      && (exam.type === 'inProgress' || exam.type === 'treated')
      && (service.has_reevaluation || service.has_observation_lift) && !isReevaluationOrLift;
    const hasReevaluationExamsList = service.reevaluationExams && service.reevaluationExams.length > 0;
    return (
    <>
      {
        (
          (service.exam_type === 'laboratory' || (service.type === "Battery" && !service.exam_type))
          && (exam.unfinishedExams && exam.unfinishedExams.find(exam => service.id === exam.service_id)))
          ?
          <Tag className={`mr-5 ${exam.type === 'inProgress' ? service.sign : service.state}`}>
            {(service.type === "Battery" && !service.exam_type) ? service.name : 'Laboratorio'}
            {
              (exam.unfinishedExams && exam.unfinishedExams.length > 0) &&
              <Popover
                title='Exámenes pendientes'
                content={exam.unfinishedExams.map(unfinishedExam =>
                  <p key={`${unfinishedExam.service_id}-${exam.id}`}>
                    {unfinishedExam.name}
                  </p>)}
                overlayClassName="medical-consultation-tooltip"
              >
                <ExclamationCircleFilled
                  style={{
                    color: "#D02A2A",
                    fontSize: 14,
                    marginLeft: '9px',
                  }}
                />
              </Popover>
            }
          </Tag>
          : <Tag className={`mr-5 ${exam.type === 'inProgress' && isAuditSignExamSymbolActive ? service.sign : service.state} ${isReevaluationPill ? 'reevaluation-tag' : ''}`}>
            <Row className="tag-content-wrapper">
              {service.auto_generated_consultation && (
                <Tooltip title={t('medicalConsultations.consultationTabs.autoGenerated')}>
                  <Col className="mr-5 auditor-icon-wrapper auto-generated-consultation-state">
                    <span className="auto-generated-badge-letter">A</span>
                  </Col>
                </Tooltip>
              )}
              {service.name}
              {isAuditSignExamSymbolActive && service.sign !== 'notEvaluated' && (service.sign === 'signed' || (!service.has_expert_fields || service.expert_signed)) &&
                <Col
                  className={`ml-5 auditor-icon-wrapper ${service.sign === 'signed' ? 'auditor-sign-completed-state' : 'auditor-sign-pending-state'}`}
                >
                  <span className="auditor-sign-letter">FA</span>
                </Col>
              }
              {isReevaluationOrLift && (exam.type === 'reevaluation' || exam.type === 'treated') && (
                hasReevaluationExamsList
                  ? (
                    <Popover
                      title={service.is_reevaluation
                        ? t('medicalConsultations.consultationTabs.reevaluationExamsTitle')
                        : t('medicalConsultations.consultationTabs.observationLiftExamsTitle')}
                      content={service.reevaluationExams.map((reevaluationExam) => (
                        <p key={`${reevaluationExam.id}-${exam.id}`}>
                          {reevaluationExam.name}
                        </p>
                      ))}
                      overlayClassName="medical-consultation-tooltip"
                    >
                      <SyncOutlined className="ml-5 reevaluation-cycle-icon" />
                    </Popover>
                  )
                  : (
                    <Tooltip title={reevaluationTypeLabel(service)}>
                      <SyncOutlined className="ml-5 reevaluation-cycle-icon" />
                    </Tooltip>
                  )
              )}
              {isOriginConsultation && (
                <Tooltip
                  title={[
                    service.has_reevaluation && t('medicalConsultations.consultationTabs.originReevaluationTag'),
                    service.has_observation_lift && t('medicalConsultations.consultationTabs.originObservationLiftTag'),
                  ].filter(Boolean).join(' / ')}
                >
                  <SyncOutlined className="ml-5 revaluation-icon" />
                </Tooltip>
              )}
            </Row>
          </Tag>
      }
    </>)
  }

  const customTableHeader = (type) => {
    return (
      <Row className="table-header">
        <Col
          lg={type === 'firstColumns' ? 3 : 3}
          xs={12}
          className="align-item-center-start p-10"
        >
          <h3>{type === 'firstColumns' ? t('medicalConsultations.consultationTabs.patient') : 'Fecha'}</h3>
        </Col>
        <Col
          lg={type === 'firstColumns' ? 3 : 6}
          xs={12}
          className="center-item p-10"
        >
          <h3 className="text-center">
            {type === 'firstColumns' ? 'Identificador' : t('medicalConsultations.consultationTabs.patient')}
          </h3>
        </Col>
        <Col
          lg={type === 'firstColumns' ? 7 : 4}
          xs={24}
          className="center-item p-10"
        >
          <h3>{type === 'firstColumns' ? t('medicalConsultations.consultationTabs.services') : 'Identificador'}</h3>
        </Col>
        <Col
          lg={type === 'firstColumns' ? 3 : 7}
          xs={type === 'firstColumns' ? 12 : 24}
          className="center-item p-10"
        >
          <h3>{type === 'firstColumns' ? 'Tiempo en sistema' : t('medicalConsultations.consultationTabs.services')}</h3>
        </Col>
        <Col
          lg={type === 'firstColumns' ? 3 : 4}
          xs={12}
          className="center-item p-10"
        >
          <h3 className="text-center">{type === 'firstColumns' ? 'Tiempo exámenes completados' : 'Dr. asignado'}</h3>
        </Col>
        {
          type === 'firstColumns' &&
          <>
            <Col
              lg={3}
              xs={12}
              className="center-item p-10"
            >
              <h3 className="text-center">Dr. asignado</h3>
            </Col>
            <Col
              lg={2}
              xs={24}
              className="center-item p-10"
            />
          </>
        }
      </Row>
    );
  };

  const customTableBody = (type, item, index, currentTab) => {
    const checkTagServices = () => {
      const laboratoryServices = item.services.filter((service) => service.exam_type === 'laboratory');
      const servicesStates = uniqBy(laboratoryServices, 'state');
      const services = item.services.filter((service) => service.exam_type !== 'laboratory');

      if (servicesStates && servicesStates.length > 0) {
        const stateResult = states.find((state) => servicesStates.find((service) => service.state === state));
        const groupedLaboratory = servicesStates.find((service) => service.state === stateResult);
        if (groupedLaboratory) {
          const reevaluationExams = laboratoryServices.filter((laboratoryService) =>
            laboratoryService.is_reevaluation || laboratoryService.is_observation_lift);
          services.push({
            ...groupedLaboratory,
            name: groupedExams.laboratory,
            is_reevaluation: reevaluationExams.some((reevaluationExam) => reevaluationExam.is_reevaluation),
            is_observation_lift: reevaluationExams.some((reevaluationExam) => reevaluationExam.is_observation_lift),
            reevaluationExams,
          });
        }
      }
      if (type === 'firstColumns') {
        return (
          <Row className="mb-0">
            <Col>
              {
                services.map((ele) => labTag(item, ele))
              }
            </Col>
          </Row>
        );
      } else {
        return (
          <Row className="mb-0">
            <Col>
              {item.services.map((ele, inx) => (
                <Fragment key={index}>
                  {ele.consultation_detail_id ? (
                    <HasAccess
                      roles={['super_usuario', 'equipo_psicologia', 'doctor',
                        'branch_office_manager', 'direccion_medica']}
                      permissions='medical-consultation-detail'
                      renderAuthFailed={
                        labTag(item, ele)
                      }
                    >
                      <Link
                        to={{
                          pathname: `/consulta-medica/${ele.consultation_detail_id}`,
                          state: {
                            from: location.pathname,
                            search: location.search,
                          },
                        }}
                        key={inx}
                        className="mb-5"
                      >
                        {labTag(item, ele)}
                      </Link>
                    </HasAccess>
                  ) : (
                    <HasAccess
                      roles={['super_usuario', 'equipo_psicologia', 'doctor',
                        'branch_office_manager', 'direccion_medica']}
                      permissions='medical-consultation-detail'
                      renderAuthFailed={
                        labTag(item, ele)
                      }
                    >
                      <Link
                        to={{
                          pathname: `/consulta-medica`,
                          search: `?admissionPatient=${item.id}&serviceId=${ele.id}&serviceType=${ele.type}`,
                          state: {
                            from: location.pathname,
                            search: location.search,
                          },
                        }}
                        key={inx}
                        className="mb-5"
                      >
                        {labTag(item, ele)}
                      </Link>
                    </HasAccess>
                  )}
                </Fragment>
              ))}
            </Col>
          </Row>
        );
      }

    };

    const actionButtonService = item.services && item.services.length > 0 ? item.services[0] : undefined;


    return (
      <Row className="table-body mb-15" key={index}>
        <Col
          lg={type === 'firstColumns' ? 3 : 3}
          xs={12}
          className="align-item-center-start p-10"
        >
          <h3 className="hide-desktop text-center">
            {
              type === 'firstColumns' ?
                t('medicalConsultations.consultationTabs.patientCol')
                :
                'Fecha'
            }
          </h3>
          {
            type === 'firstColumns' ?
              <Row className="patient-date-wrapper">
                <div
                  className={`called-doctor ${item.called_doctor === true && "yellow-background"}`}
                />
                <p className="mb-0">
                  {item.patient_full_name}
                  {form && form.getFieldValue("allBranchOffices") && (
                    <Tag color="processing">{item.branch_office_name}</Tag>
                  )}
                  <HasAccess
                    roles={['super_usuario', 'equipo_medico', 'equipo_psicologia', 'doctor', 'branch_office_manager']}
                    permission="call-patient"
                  >
                    {hasBoxes && (
                      <div className="mt-5">
                        <Popover
                          content={!moment(item.admission_date, 'YYYY-MM-DD').isSame(moment(), 'day') ? t('examPage.examTabs.callPatientDisabledTooltip') : ''}
                          placement="top"
                        >
                          <span>
                            <Button
                              loading={loadingPerRow[item.id]}
                              disabled={!moment(item.admission_date, 'YYYY-MM-DD').isSame(moment(), 'day') || loadingPerRow[item.id]}
                              onClick={() => handleCallPatient(item.id)}
                              className="cmt-button secondary mt-5 call-patient-button"
                              type="primary"
                              size="small"
                            >
                              {t('examPage.examTabs.callPatient')}
                            </Button>
                          </span>
                        </Popover>
                      </div>
                    )}
                  </HasAccess>
                </p>
              </Row>
              :
              <p className="mb-0">
                {formatDate(item.admission_date)}
              </p>
          }
        </Col>
        <Col
          lg={type === 'firstColumns' ? 3 : 6}
          xs={12}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">
            {
              type === 'firstColumns' ?
                'Identificador'
                :
                t('medicalConsultations.consultationTabs.patientCol')
            }
          </h3>
          {
            type === 'firstColumns' ?
              <p className="mb-0">{item.document_type === "rut" ? format(item.document_number) : item.document_number}</p>
              :
              <>
                <div className="call-patient-wrapper">
                  {item.patient_full_name}
                  {form && form.getFieldValue("allBranchOffices") && (
                    <Tag color="processing">{item.branch_office_name}</Tag>
                  )}
                  <HasAccess
                    roles={['super_usuario', 'equipo_medico', 'equipo_psicologia', 'doctor', 'branch_office_manager']}
                    permission="call-patient"
                  >
                    {hasBoxes && (
                      <div className="mt-5">
                        <Popover
                          content={!moment(item.admission_date, 'YYYY-MM-DD').isSame(moment(), 'day') ? t('examPage.examTabs.callPatientDisabledTooltip') : ''}
                          placement="top"
                        >
                          <span>
                            <Button
                              loading={loadingPerRow[item.id]}
                              disabled={!moment(item.admission_date, 'YYYY-MM-DD').isSame(moment(), 'day') || loadingPerRow[item.id]}
                              onClick={() => handleCallPatient(item.id)}
                              className="cmt-button secondary mt-5  call-patient-button"
                              type="primary"
                              size="small"
                            >
                              {t('examPage.examTabs.callPatient')}
                            </Button>
                          </span>
                        </Popover>
                      </div>
                    )}
                  </HasAccess>
                </div>
              </>
          }
        </Col>
        <Col
          lg={type === 'firstColumns' ? 7 : 4}
          xs={24}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">{type === 'firstColumns' ? 'Baterías o exámenes' : 'Identificador'}</h3>
          {
            type === 'firstColumns' ?
              checkTagServices()
              :
              <p className="mb-0">
                {
                  item.document_type === "rut" ? format(item.document_number) : item.document_number
                }
              </p>
          }
        </Col>
        <Col
          lg={type === 'firstColumns' ? 3 : 7}
          xs={type === 'firstColumns' ? 12 : 24}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">
            {type === 'firstColumns' ? 'Tiempo en sistema' : 'Baterías o exámenes'}
          </h3>
          {
            type === 'firstColumns' ?
              <p className="mb-0 text-center">
                {item.created_at && timeDiff(item.created_at, undefined, item.timezone, true)}
              </p>
              :
              checkTagServices()
          }
        </Col>
        <Col
          lg={type === 'firstColumns' ? 3 : 4}
          xs={type === 'firstColumns' ? 12 : 24}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">{type === 'firstColumns' ? 'Tiempo exámenes completados' : 'Dr. asignado'}</h3>
          {
            type === 'firstColumns' ?
              <p className="mb-0">
                {item.date_done && formatTime(item.date_done, "diference", item.timezone)}
              </p>
              :
              <p className="mb-0">{item.assigned_doctor}</p>
          }
        </Col>
        {
          type === 'firstColumns' &&
          <>
            <Col
              lg={3}
              xs={24}
              className="center-item p-10"
            >
              <h3 className="hide-desktop text-center">Dr. asignado</h3>
              <p className="mb-0">
                {item.assigned_doctor && item.assigned_doctor}&nbsp;
                {(item.assigned_doctor && item.assigned_at) && formatTime(item.assigned_at, null, item.timezone)}
              </p>
            </Col>
            <Col
              lg={2}
              xs={24}
              className="center-item p-10"
            >
              {
                (item.type === "pending" && !item.services[0]?.consultation_detail_id) ?
                  (
                    <Link
                      to={{
                        pathname: `/consulta-medica`,
                        search: `?admissionPatient=${item.id}&serviceId=${actionButtonService.id}&serviceType=${actionButtonService.type}`,
                        state: { from: location.pathname, search: location.search }
                      }}
                    >
                      <HasAccess
                        roles={['super_usuario', 'equipo_psicologia', 'doctor',
                          'branch_office_manager', 'direccion_medica'
                        ]}
                        permissions='medical-consultation-detail'
                      >
                        <Button
                          className="cmt-button secondary"
                          type="primary"
                          size="small"
                          id="goToConsultation"
                        >
                          {currentTab === 'reevaluation' ? 'Reevaluar' : 'Ver más'}
                        </Button>
                      </HasAccess>
                    </Link>
                  )
                  :
                  (
                    <Link
                      to={{
                        pathname: `/consulta-medica/${item.services[0]['consultation_detail_id']}`,
                        state: {
                          from: location.pathname,
                          search: location.search
                        }
                      }}
                    >
                      <HasAccess
                        roles={['super_usuario', 'equipo_psicologia', 'doctor',
                          'branch_office_manager', 'direccion_medica']}
                        permissions='medical-consultation-detail'
                      >
                        <Button
                          className="cmt-button secondary"
                          type="primary"
                          size="small"
                        >
                          {currentTab === 'reevaluation' ? 'Reevaluar' : 'Ver más'}
                        </Button>
                      </HasAccess>
                    </Link>
                  )
              }
            </Col>
          </>
        }
      </Row>
    );
  };


  return (
    <Row justify="center">
      <Col lg={24} xs={24}>
        <Tabs className="cmt-tabs" defaultActiveKey="1">
          <TabPane
            key={"1"}
            tab={
              <Badge
                showZero
                offset={[15, 0]}
                count={
                  pendingConsultations
                    ? pendingConsultations.pagination.total
                    : 0
                }
              >
                <p className="mb-0">{t('medicalConsultations.consultationTabs.onHoldTitle')}</p>
              </Badge>
            }
          >
            <Row justify="center">
              <Col lg={22} xs={22}>
                <Row>
                  <Col lg={24} xs={24}>
                    {loading.pending ? (
                      <Row justify="center">
                        <Spin className="custom-spin mt-40 mb-40" />
                      </Row>
                    ) : pendingConsultations &&
                      pendingConsultations.data.length > 0 ? (
                      <Row>
                        <Col lg={24} xs={24}>
                          {consultationSymbols("state")}
                        </Col>
                        <Col lg={24} xs={24}>
                          {customTableHeader('firstColumns')}
                          {
                            pendingConsultations.data.map((item, index) =>
                              customTableBody('firstColumns', item, index, 'pending')
                            )
                          }
                        </Col>
                        <Col lg={24} xs={24}>
                          <Row className="pagination-wrapper mt-30 mb-40">
                            <Col>
                              <Radio.Group className="cmt-pagination">
                                <Radio.Button
                                  value="prev"
                                  disabled={currentPage.pending === 1}
                                  onClick={(event) =>
                                    loadMore(event, "pending")
                                  }
                                >
                                  Anterior
                                </Radio.Button>
                                <Radio.Button
                                  value="next"
                                  disabled={
                                    !pendingConsultations ||
                                    pendingConsultations.data.length <
                                    pendingConsultations.pagination.perPage
                                  }
                                  onClick={(event) =>
                                    loadMore(event, "pending")
                                  }
                                >
                                  Siguiente
                                </Radio.Button>
                              </Radio.Group>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    ) : (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                          <p className="text-center">
                            {t('medicalConsultations.consultationTabs.onHoldDisclaimer')}
                          </p>
                        }
                      />
                    )}
                  </Col>
                </Row>
              </Col>
            </Row>
          </TabPane>
          {
            countryRestrictions && countryCode && countryRestrictions[countryCode]
            && !countryRestrictions[countryCode].find(item => item === 'reevaluation') &&
            <TabPane
              key={"2"}
              tab={
                <Badge
                  showZero
                  offset={[15, 0]}
                  count={
                    reevaluationConsultations
                      ? reevaluationConsultations.pagination.total
                      : 0
                  }
                >
                  <p className="mb-0">{t('medicalConsultations.consultationTabs.reevaluationsTitle')}</p>
                </Badge>
              }
            >
              <Row justify="center">
                <Col lg={22} xs={22}>
                  <Row>
                    <Col lg={24} xs={24}>
                      {loading.reevaluation ? (
                        <Row justify="center">
                          <Spin className="custom-spin mt-40 mb-40" />
                        </Row>
                      ) : reevaluationConsultations &&
                        reevaluationConsultations.data.length > 0 ? (
                        <Row>
                          <Col lg={24} xs={24}>
                            {consultationSymbols("state")}
                          </Col>
                          <Col lg={24} xs={24}>
                            {customTableHeader('firstColumns')}
                            {
                              reevaluationConsultations.data.map((item, index) =>
                                customTableBody('firstColumns', item, index, 'reevaluation')
                              )
                            }
                          </Col>
                          <Col lg={24} xs={24}>
                            <Row className="pagination-wrapper mt-30 mb-40">
                              <Col>
                                <Radio.Group className="cmt-pagination">
                                  <Radio.Button
                                    value="prev"
                                    disabled={currentPage.reevaluation === 1}
                                    onClick={(event) =>
                                      loadMore(event, "reevaluation")
                                    }
                                  >
                                    Anterior
                                  </Radio.Button>
                                  <Radio.Button
                                    value="next"
                                    disabled={
                                      !reevaluationConsultations ||
                                      reevaluationConsultations.data.length <
                                      reevaluationConsultations.pagination
                                        .perPage
                                    }
                                    onClick={(event) =>
                                      loadMore(event, "reevaluation")
                                    }
                                  >
                                    Siguiente
                                  </Radio.Button>
                                </Radio.Group>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      ) : (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={
                            <p className="text-center">
                              {t('medicalConsultations.consultationTabs.reevaluationsDisclaimer')}
                            </p>
                          }
                        />
                      )}
                    </Col>
                  </Row>
                </Col>
              </Row>
            </TabPane>
          }
          <TabPane
            key={"3"}
            tab={
              <Badge
                showZero
                offset={[15, 0]}
                count={
                  inProgressConsultations
                    ? inProgressConsultations.pagination.total
                    : 0
                }
              >
                <p className="mb-0">{t('medicalConsultations.consultationTabs.inProgressTitle')}</p>
              </Badge>
            }
          >
            <Row justify="center">
              <Col lg={22} xs={22}>
                <Row>
                  <Col lg={24} xs={24}>
                    {loading.inProgress ? (
                      <Row justify="center">
                        <Spin className="custom-spin mt-40 mb-40" />
                      </Row>
                    ) : inProgressConsultations &&
                      inProgressConsultations.data.length > 0 ? (
                      <Row>
                        <Col lg={24} xs={24}>
                          {consultationSymbols("inProgress")}
                        </Col>
                        <Col lg={24} xs={24}>
                          {customTableHeader('secondColumns')}
                          {
                            inProgressConsultations.data.map((item, index) =>
                              customTableBody('secondColumns', item, index, 'inProgress')
                            )
                          }
                        </Col>
                        <Col lg={24} xs={24}>
                          <Row className="pagination-wrapper mt-30 mb-40">
                            <Col>
                              <Radio.Group className="cmt-pagination">
                                <Radio.Button
                                  value="prev"
                                  disabled={currentPage.inProgress === 1}
                                  onClick={(event) =>
                                    loadMore(event, "inProgress")
                                  }
                                >
                                  Anterior
                                </Radio.Button>
                                <Radio.Button
                                  value="next"
                                  disabled={
                                    !inProgressConsultations ||
                                    inProgressConsultations.data.length <
                                    inProgressConsultations.pagination.perPage
                                  }
                                  onClick={(event) =>
                                    loadMore(event, "inProgress")
                                  }
                                >
                                  Siguiente
                                </Radio.Button>
                              </Radio.Group>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    ) : (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                          <p className="text-center">
                            {t('medicalConsultations.consultationTabs.inProgressDisclaimer')}
                          </p>
                        }
                      />
                    )}
                  </Col>
                </Row>
              </Col>
            </Row>
          </TabPane>

          <TabPane
            key={"4"}
            tab={
              <Badge
                showZero
                offset={[15, 0]}
                count={
                  treatedConsultations
                    ? treatedConsultations.pagination.total
                    : 0
                }
              >
                <p className="mb-0">{t('medicalConsultations.consultationTabs.attendedTitle')}</p>
              </Badge>
            }
          >
            <Row justify="center">
              <Col lg={22} xs={22}>
                <Row>
                  <Col lg={24} xs={24}>
                    {loading.treated ? (
                      <Row justify="center">
                        <Spin className="custom-spin mt-40 mb-40" />
                      </Row>
                    ) : treatedConsultations &&
                      treatedConsultations.data.length > 0 ? (
                      <Row>
                        <Col lg={24} xs={24}>
                          {consultationSymbols("treated")}
                        </Col>
                        <Col lg={24} xs={24}>
                          {customTableHeader('secondColumns')}
                          {
                            treatedConsultations.data.map((item, index) =>
                              customTableBody('secondColumns', item, index, 'treated')
                            )
                          }
                        </Col>
                        <Col lg={24} xs={24}>
                          <Row className="pagination-wrapper mt-30 mb-40">
                            <Col>
                              <Radio.Group className="cmt-pagination">
                                <Radio.Button
                                  value="prev"
                                  disabled={currentPage.treated === 1}
                                  onClick={(event) =>
                                    loadMore(event, "treated")
                                  }
                                >
                                  Anterior
                                </Radio.Button>
                                <Radio.Button
                                  value="next"
                                  disabled={
                                    !treatedConsultations ||
                                    treatedConsultations.data.length <
                                    treatedConsultations.pagination.perPage
                                  }
                                  onClick={(event) =>
                                    loadMore(event, "treated")
                                  }
                                >
                                  Siguiente
                                </Radio.Button>
                              </Radio.Group>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    ) : (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                          <p className="text-center">
                            {t('medicalConsultations.consultationTabs.attendedDisclaimer')}
                          </p>
                        }
                      />
                    )}
                  </Col>
                </Row>
              </Col>
            </Row>
          </TabPane>
        </Tabs>
      </Col>
    </Row>
  );
};

export default ConsultationTabs;
